import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, ExternalLink, Loader2, Wallet, Globe, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import SecurityQuestionCard from "@/components/SecurityQuestionCard";

export default function Account() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [savingVis, setSavingVis] = useState(false);
  const [bio, setBio] = useState("");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/billing/history").then((r) => setHistory(r.data)).catch(() => {}),
      api.get("/collection/portfolio-value").then((r) => setPortfolio(r.data)).catch(() => {}),
      api.get("/account/profile").then((r) => {
        setProfile(r.data);
        setBio(r.data.bio || "");
        setDisplayName(r.data.display_name || "");
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const toggleVisibility = async () => {
    if (!profile) return;
    setSavingVis(true);
    try {
      const { data } = await api.post("/account/profile", { is_public: !profile.is_public });
      setProfile({ ...profile, is_public: data.is_public });
      toast.success(data.is_public ? "Profil public activé" : "Profil rendu privé");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingVis(false);
    }
  };

  const saveProfileMeta = async () => {
    try {
      const { data } = await api.post("/account/profile", { display_name: displayName, bio });
      setProfile({ ...profile, display_name: data.display_name, bio: data.bio });
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sharePublicLink = async () => {
    if (!profile) return;
    const url = `${window.location.origin}/u/${profile.id}`;
    try {
      if (navigator.share) await navigator.share({ url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien public copié");
      }
    } catch {}
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/account" });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPortalLoading(false);
    }
  };

  if (!user || !user.email) return null;

  return (
    <div className="page-enter max-w-[1000px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8">
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">// ACCOUNT</div>
        <h1 className="font-heading font-black text-5xl tracking-tighter">{user.name || user.email.split("@")[0]}</h1>
        <div className="font-mono text-xs text-white/40 mt-2 tracking-wider">{user.email}</div>
      </div>

      {/* Portfolio summary */}
      {portfolio && portfolio.item_count > 0 && (
        <div className="flat-card p-6 mb-8" data-testid="account-portfolio">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5" /> Portfolio value
            </span>
            <Link to="/collection" className="font-mono text-[10px] text-[#FACC15] hover:underline tracking-wider">Manage →</Link>
          </div>
          <div className="font-heading font-black text-5xl tracking-tighter neon-yellow leading-none">
            €{portfolio.total_value_eur.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">Owned</div>
              <div className="font-mono font-medium">€{portfolio.owned_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">Wishlist</div>
              <div className="font-mono font-medium">€{portfolio.wishlist_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">Favorites</div>
              <div className="font-mono font-medium">€{portfolio.favorite_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      )}

      {/* Public profile management */}
      {profile && (
        <div className="flat-card p-6 mb-8" data-testid="account-public-profile">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-xs text-white/50 uppercase tracking-widest flex items-center gap-2">
              {profile.is_public ? <Globe className="w-3.5 h-3.5 text-[#00E5FF]" /> : <Lock className="w-3.5 h-3.5" />}
              Public profile
            </div>
            {profile.is_public && (
              <button onClick={sharePublicLink} className="font-mono text-[10px] text-[#FACC15] hover:underline tracking-wider inline-flex items-center gap-1" data-testid="share-public-btn">
                <Share2 className="w-3 h-3" /> Share link
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="font-mono text-[10px] text-white/40 uppercase tracking-widest block mb-1">Display name</label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                data-testid="profile-display-name-input"
                className="w-full px-3 py-2 text-sm bg-transparent border border-white/20 focus:border-[#FACC15] outline-none"
                maxLength={60}
              />
            </div>
            <div>
              <label className="font-mono text-[10px] text-white/40 uppercase tracking-widest block mb-1">Bio</label>
              <input
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Collectionneur depuis 2003…"
                data-testid="profile-bio-input"
                className="w-full px-3 py-2 text-sm bg-transparent border border-white/20 focus:border-[#FACC15] outline-none"
                maxLength={280}
              />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-white/10">
            <div className="text-xs text-white/60 max-w-md">
              {profile.is_public
                ? "Votre collection est visible publiquement et apparaît dans le fil communautaire."
                : "Activez le profil public pour partager votre collection et apparaître dans le fil."}
            </div>
            <div className="flex gap-2">
              <button onClick={saveProfileMeta} data-testid="save-profile-btn" className="btn-ghost text-xs uppercase tracking-wider">
                Save
              </button>
              <button
                onClick={toggleVisibility}
                disabled={savingVis}
                data-testid="toggle-visibility-btn"
                className={`btn-primary text-xs uppercase tracking-wider inline-flex items-center gap-1.5 ${profile.is_public ? "!bg-white/10 !text-white" : ""}`}
              >
                {savingVis ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : profile.is_public ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                {profile.is_public ? "Make private" : "Make public"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription card */}
      <div className={`flat-card p-8 mb-8 ${user.is_premium ? "border-[#FACC15]/40" : ""}`} data-testid="account-subscription-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/40 mb-2">
              <Crown className={`w-3.5 h-3.5 ${user.is_premium ? "text-[#FACC15]" : ""}`} />
              Subscription
            </div>
            <div className="font-heading font-black text-3xl tracking-tighter">
              {user.is_premium ? <span className="text-[#FACC15]">Premium</span> : "Free"}
            </div>
            {user.is_premium && user.subscription_expires_at && (
              <div className="font-mono text-xs text-white/50 mt-1 tracking-wider">
                Renews / expires on {new Date(user.subscription_expires_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {user.is_premium ? (
              <button onClick={openPortal} disabled={portalLoading} data-testid="open-billing-portal-btn" className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
                {portalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                Manage with Stripe
              </button>
            ) : (
              <Link to="/pricing" data-testid="account-upgrade-btn" className="btn-primary text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5" /> Upgrade
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Billing history */}
      <div>
        <div className="font-mono text-xs text-white/50 uppercase tracking-widest mb-3">// BILLING_HISTORY</div>
        {loading ? (
          <div className="text-center text-white/40 font-mono py-12 tracking-widest text-xs">LOADING…</div>
        ) : history.length === 0 ? (
          <div className="flat-card p-10 text-center text-white/40 text-sm">No payments yet.</div>
        ) : (
          <div className="flat-card overflow-hidden">
            <ul className="divide-y divide-white/10">
              {history.map((h) => (
                <li key={h.session_id} className="px-5 py-3 grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center text-sm">
                  <div>
                    <div className="font-heading font-semibold capitalize">Premium · {h.plan}</div>
                    <div className="font-mono text-[10px] text-white/40 tracking-wider">{h.created_at?.slice(0, 10)}</div>
                  </div>
                  <span className="font-mono">€{Number(h.amount).toFixed(2)}</span>
                  <span className={`lang-chip ${h.payment_status === "paid" ? "text-[#FACC15] border-[#FACC15]/40" : ""}`}>
                    {h.payment_status || "initiated"}
                  </span>
                  <span className="font-mono text-[10px] text-white/30 truncate max-w-[140px]">{h.session_id?.slice(0, 14)}…</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Security: change password + secret question */}
      <div className="mt-8">
        <ChangePasswordCard />
        <SecurityQuestionCard />
      </div>
    </div>
  );
}
